/** @jsx jsx */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { css, jsx } from '@compiled/react';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { token } from '@atlaskit/tokens';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { JiraModal as Modal } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ConfirmBeforeSaveProps } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/confirm-before-save.tsx';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PACKAGE_NAME, TEAM_NAME } from '../../common/constants.tsx';
import { ProformaStoreContext } from '../../common/utils/proforma/context/index.tsx';
import {
	getDuplicateJiraFields,
	getProformaFormId,
	getProformaFormName,
} from '../../common/utils/proforma/state/selectors.tsx';
import messages from './messages.tsx';
import { removeLinkedFields } from './utils.tsx';

export const DuplicateFormLinkedFieldsWarningDialog = (props: ConfirmBeforeSaveProps) => {
	const { isOpen, onConfirm, activeLayoutItems } = props;
	const { data: projectContext } = useProjectContext();

	const store = useContext(ProformaStoreContext);
	const state = store?.getState();

	const duplicateJiraFieldsInForm =
		state && activeLayoutItems ? getDuplicateJiraFields(state, activeLayoutItems) : [];

	const hasDuplicateJiraFields = duplicateJiraFieldsInForm.length > 0;

	const formId = state ? getProformaFormId(state) : undefined;
	const formName = state ? getProformaFormName(state) : undefined;

	useEffect(() => {
		if (isOpen && !hasDuplicateJiraFields) {
			onConfirm(undefined);
		}
	}, [isOpen, onConfirm, hasDuplicateJiraFields]);

	if (hasDuplicateJiraFields && formId && projectContext?.projectId) {
		return (
			<DuplicateFormLinkedFieldsWarningDialogInternal
				{...props}
				formName={formName}
				formId={formId}
				projectId={projectContext.projectId}
				duplicateJiraFieldsInForm={duplicateJiraFieldsInForm}
			/>
		);
	}

	return null;
};

type ConfirmDuplicateFieldRemovalDialogProps = ConfirmBeforeSaveProps & {
	duplicateJiraFieldsInForm: { name: string; key: string }[];
	formId: number;
	formName?: string;
	projectId: number;
};

export const DuplicateFormLinkedFieldsWarningDialogInternal = (
	props: ConfirmDuplicateFieldRemovalDialogProps,
) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagService();
	const [isRemovingFields, setIsRemovingFields] = useState(false);

	const {
		isOpen,
		onCancel,
		onConfirm,
		formName,
		duplicateJiraFieldsInForm = [],
		formId,
		projectId,
	} = props;
	const cloudId = useCloudId();
	const hasDuplicateJiraFields = duplicateJiraFieldsInForm.length > 0;

	const removeFields = useCallback(async () => {
		try {
			await removeLinkedFields(
				cloudId,
				projectId,
				formId,
				duplicateJiraFieldsInForm.map((field) => field.key),
			);
			onConfirm();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				meta: {
					id: 'removeDuplicateLinkedFieldsFromFormError',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});
			showFlag({
				id: 'project-settings.issue-type-page-product-features-jsm-common.ui.duplicate-form-linked-fields-warning-dialogerror-removing-duplicate-form-fields',
				type: 'error',
				title: formatMessage(messages.errorRemovingDuplicateFormFields),
				description: formatMessage(messages.errorRemovingDuplicateFormFieldsDescription),
				isAutoDismiss: true,
				messageId:
					'project-settings-product-features-jsm-common.ui.duplicate-form-linked-fields-warning-dialog.show-flag.error',
				messageType: 'transactional',
			});
		} finally {
			setIsRemovingFields(false);
		}
	}, [cloudId, projectId, formId, duplicateJiraFieldsInForm, onConfirm, showFlag, formatMessage]);

	const handleConfirm = useCallback(
		(_: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'removeFieldDuplicateFormLinkedFieldsWarningDialog', {
				duplicateJiraFieldsInFormCount: duplicateJiraFieldsInForm?.length,
			});

			setIsRemovingFields(true);
			removeFields();
		},
		[duplicateJiraFieldsInForm, removeFields],
	);

	const handleCancel = useCallback(
		(_: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'cancelDuplicateFormLinkedFieldsWarningDialog', {
				duplicateJiraFieldsInFormCount: duplicateJiraFieldsInForm?.length,
			});
			onCancel();
		},
		[duplicateJiraFieldsInForm, onCancel],
	);

	if (duplicateJiraFieldsInForm.length === 1) {
		return (
			<ModalTransition>
				{isOpen && (
					<Modal
						onClose={onCancel}
						messageId="project-settings-product-features-jsm-common.ui.duplicate-form-linked-fields-warning-dialog.modal"
						messageType="transactional"
					>
						<ModalHeader>
							<ModalTitle appearance="warning">{formatMessage(messages.title)}</ModalTitle>
						</ModalHeader>
						<ModalBody>
							{formatMessage(messages.singleDuplicateDescription, {
								formField: duplicateJiraFieldsInForm[0].name,
								formName,
							})}
						</ModalBody>
						<ModalFooter>
							<Button appearance="subtle" onClick={handleCancel}>
								{formatMessage(messages.cancel)}
							</Button>
							<LoadingButton
								appearance="warning"
								onClick={handleConfirm}
								isLoading={isRemovingFields}
							>
								{formatMessage(messages.removeField)}
							</LoadingButton>
						</ModalFooter>
					</Modal>
				)}
			</ModalTransition>
		);
	}

	return (
		<ModalTransition>
			{isOpen && hasDuplicateJiraFields && (
				<Modal
					messageId="project-settings-product-features-jsm-common.ui.duplicate-form-linked-fields-warning-dialog.modal.1"
					messageType="transactional"
				>
					<ModalHeader>
						<ModalTitle appearance="warning">{formatMessage(messages.title)}</ModalTitle>
					</ModalHeader>
					<ModalBody>
						{duplicateJiraFieldsInForm.length <= 5 ? (
							// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
							<p>
								{formatMessage(messages.multipleDuplicatesDescription, {
									formName,
								})}
								<ul css={listStyle}>
									{duplicateJiraFieldsInForm.map((formField) => (
										<li key={formField.name}>{formField.name}</li>
									))}
								</ul>
							</p>
						) : (
							// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
							<p>
								{formatMessage(messages.genericDuplicatesDescription, {
									formName,
								})}
							</p>
						)}
					</ModalBody>
					<ModalFooter>
						<Button appearance="subtle" onClick={handleCancel}>
							{formatMessage(messages.cancel)}
						</Button>
						<LoadingButton
							appearance="warning"
							onClick={handleConfirm}
							isLoading={isRemovingFields}
						>
							{formatMessage(messages.removeFields)}
						</LoadingButton>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};

export const DuplicateFormLinkedFieldsWarningDialogWithErrorBoundary = (
	props: ConfirmBeforeSaveProps,
) => (
	<JSErrorBoundary
		id="duplicate-dialog"
		packageName="jiraProjectSettingsProductFeaturesJsmCommon"
		teamName="jsm-request-intake"
		fallback="flag"
	>
		<DuplicateFormLinkedFieldsWarningDialog {...props} />
	</JSErrorBoundary>
);

const listStyle = css({
	paddingLeft: token('space.400'),
	paddingTop: token('space.050'),
});
